import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { MagnifyingGlass, Faders, X, Star, Storefront, GridFour, List as ListIcon } from '@phosphor-icons/react';
import { LazyImage } from '../components/LazyImage';
import { ListingCard } from '../components/ListingCard';
import { useViewMode } from '../utils/useViewMode';
import { formatDualCurrency } from '../utils/currency';
import { useCurrency } from '../context/CurrencyContext';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { Header } from '../components/Header';
import { BottomNav } from '../components/BottomNav';
import { FavoriteButton } from '../components/FavoriteButton';
import { FeaturedBadge } from '../components/FeaturedBadge';
import { ListingStatusBadge } from '../components/ListingStatusBadge';
import { RecentlyViewed } from '../components/RecentlyViewed';
import { NumberInput } from '../components/NumberInput';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { setCategoryCache, getCategoryName, getSubcategoryName } from '../utils/categoryNames';
import { SubcategoryInfoIcon } from '../components/SubcategoryInfoIcon';
import { Plus } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BrowsePage = () => {
  const { t } = useTranslation();
  const { secondaryCurrency } = useCurrency();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [subcategory, setSubcategory] = useState(searchParams.get('subcategory') || 'all');
  const [city, setCity] = useState(searchParams.get('city') || 'all');
  const [sortPrice, setSortPrice] = useState(searchParams.get('sort') || 'default');
  const [minPrice, setMinPrice] = useState(() => {
    const v = searchParams.get('min_price');
    return v ? parseInt(v, 10) || '' : '';
  });
  const [maxPrice, setMaxPrice] = useState(() => {
    const v = searchParams.get('max_price');
    return v ? parseInt(v, 10) || '' : '';
  });
  const [region, setRegion] = useState(searchParams.get('region') || 'all');
  const [proOnly, setProOnly] = useState(searchParams.get('pro') === '1');

  const [cities, setCities] = useState([]);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [viewMode, setViewMode] = useViewMode();

  const LISTINGS_PER_PAGE = 9;

  // Admin-defined dynamic Search Filters (scoped to category/subcategory)
  const [dynamicFilters, setDynamicFilters] = useState([]);
  const [dynamicValues, setDynamicValues] = useState({});

  // Auto-open filters if coming from landing page filter button
  useEffect(() => {
    if (searchParams.get('open_filters') === '1') {
      setShowMobileFilters(true);
      const p = new URLSearchParams(searchParams);
      p.delete('open_filters');
      setSearchParams(p, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCat = categories.find(c => c.value === category);
  const subcatOptions = selectedCat?.subcategories || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [citiesRes, categoriesRes, reRes] = await Promise.all([
          axios.get(`${API}/cities`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/real-estate-filters`)
        ]);
        setCities(citiesRes.data);
        setCategories(categoriesRes.data);
        setCategoryCache(categoriesRes.data);
        setRegions(reRes.data?.regions || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const urlPage = parseInt(searchParams.get('page')) || 1;
    setPage(urlPage);
    setListings([]);
    fetchListings(urlPage);
    // Scroll to top when page/filters change
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch admin-defined search filters when category/subcategory changes
  useEffect(() => {
    if (!category || category === 'all') {
      setDynamicFilters([]);
      setDynamicValues({});
      return;
    }
    const params = new URLSearchParams({ category });
    if (subcategory && subcategory !== 'all') params.append('subcategory', subcategory);
    axios.get(`${API}/search-filters?${params.toString()}`)
      .then(res => {
        setDynamicFilters(res.data || []);
        // Seed values from URL if present
        const sp = searchParams;
        const seeded = {};
        (res.data || []).forEach(f => {
          if (f.type === 'range') {
            seeded[`min_${f.key}`] = sp.get(`sf_min_${f.key}`) || '';
            seeded[`max_${f.key}`] = sp.get(`sf_max_${f.key}`) || '';
          } else {
            seeded[f.key] = sp.get(`sf_${f.key}`) || (f.type === 'dropdown' ? 'all' : '');
          }
        });
        setDynamicValues(seeded);
      })
      .catch(() => setDynamicFilters([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, subcategory]);

  const fetchListings = async (pageNum = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const sp = searchParams;
      if (sp.get('search')) params.append('search', sp.get('search'));
      if (sp.get('category') && sp.get('category') !== 'all') params.append('category', sp.get('category'));
      if (sp.get('subcategory') && sp.get('subcategory') !== 'all') params.append('subcategory', sp.get('subcategory'));
      if (sp.get('city') && sp.get('city') !== 'all') params.append('city', sp.get('city'));
      if (sp.get('sort') && sp.get('sort') !== 'default') params.append('sort', sp.get('sort'));
      if (sp.get('min_price')) params.append('min_price', sp.get('min_price'));
      if (sp.get('max_price')) params.append('max_price', sp.get('max_price'));
      if (sp.get('region') && sp.get('region') !== 'all') params.append('region', sp.get('region'));
      if (sp.get('pro') === '1') params.append('seller_type', 'business');
      // Forward all admin-defined search filters (sf_*, sf_min_*, sf_max_*)
      for (const [k, v] of sp.entries()) {
        if (k.startsWith('sf_') && v && v !== 'all') params.append(k, v);
      }
      params.append('page', pageNum);
      params.append('limit', LISTINGS_PER_PAGE);

      const { data } = await axios.get(`${API}/listings?${params.toString()}`);
      const items = data.listings || data;
      setListings(items);
      setTotalPages(data.pages || 1);
      setTotalResults(data.total || items.length);
      setPage(pageNum);
    } catch (error) {
      toast.error(t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    const params = new URLSearchParams(searchParams);
    if (newPage === 1) params.delete('page');
    else params.set('page', newPage);
    setSearchParams(params);
  };

  // Keyboard arrow pagination — ← Prev / → Next
  useEffect(() => {
    if (totalPages <= 1) return;
    const onKey = (e) => {
      // Skip when user is typing in an input/textarea/contenteditable or a modal is open
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable) return;
      if (document.querySelector('[role="dialog"]')) return;  // skip when any modal/dialog is open
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goToPage(page - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goToPage(page + 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (category !== 'all') params.set('category', category);
    if (subcategory !== 'all') params.set('subcategory', subcategory);
    if (city !== 'all') params.set('city', city);
    if (sortPrice !== 'default') params.set('sort', sortPrice);
    if (minPrice !== '' && minPrice != null) params.set('min_price', String(minPrice));
    if (maxPrice !== '' && maxPrice != null) params.set('max_price', String(maxPrice));
    if (region !== 'all') params.set('region', region);
    if (proOnly) params.set('pro', '1');
    // Admin-defined dynamic filters
    dynamicFilters.forEach(f => {
      if (f.type === 'range') {
        const mn = dynamicValues[`min_${f.key}`];
        const mx = dynamicValues[`max_${f.key}`];
        if (mn) params.set(`sf_min_${f.key}`, mn);
        if (mx) params.set(`sf_max_${f.key}`, mx);
      } else {
        const v = dynamicValues[f.key];
        if (v && v !== 'all' && v !== '') params.set(`sf_${f.key}`, v);
      }
    });
    setSearchParams(params);
    setShowMobileFilters(false);
  };

  const handleCategoryChange = (val) => {
    setCategory(val);
    setSubcategory('all');
    setDynamicValues({});
  };

  const handleRegionChange = (val) => {
    setRegion(val);
    setCity('all');
  };

  const clearFilters = () => {
    setSearch(''); setCategory('all'); setSubcategory('all'); setCity('all'); setSortPrice('default');
    setMinPrice(''); setMaxPrice(''); setRegion('all');
    setProOnly(false);
    setDynamicValues({});
    setSearchParams(new URLSearchParams());
    setShowMobileFilters(false);
  };

  const hasActiveFilters = search || category !== 'all' || subcategory !== 'all' || city !== 'all' || minPrice || maxPrice || region !== 'all' || proOnly;

  const handleSaveSearch = async () => {
    if (!user) { toast.error(t('common.loginRequired')); return; }
    try {
      await axios.post(`${API}/saved-searches`, {
        search: search || null,
        category: category !== 'all' ? category : null,
        subcategory: subcategory !== 'all' ? subcategory : null,
        city: city !== 'all' ? city : null,
        sort: sortPrice !== 'default' ? sortPrice : null,
      }, { withCredentials: true });
      toast.success(t('savedSearches.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('savedSearches.saveError'));
    }
  };

  /* ─── Admin-defined Dynamic Filters (scoped to category/subcategory) ─── */
  const renderDynamicFilters = (isMobile = false) => {
    if (!dynamicFilters.length) return null;
    const setVal = (k, v) => setDynamicValues(prev => ({ ...prev, [k]: v }));
    return (
      <div className="border-t border-border pt-4" data-testid="dynamic-filters-section">
        {!isMobile && (
          <p className="text-xs font-medium text-text_muted uppercase tracking-wider mb-2">
            {getCategoryName(category, t)}{subcategory !== 'all' ? ` / ${getSubcategoryName(subcategory, t)}` : ''} — {t('listing.filters')}
          </p>
        )}
        <div className={isMobile ? 'space-y-3' : 'grid grid-cols-2 md:grid-cols-4 gap-3'}>
          {dynamicFilters.map((f) => {
            const commonLabel = <Label className="text-xs text-text_muted mb-1 block">{f.label}</Label>;
            if (f.type === 'dropdown') {
              const val = dynamicValues[f.key] || 'all';
              return (
                <div key={f.filter_id} data-testid={`dyn-filter-${f.key}`}>
                  {commonLabel}
                  <Select value={val} onValueChange={(v) => setVal(f.key, v)}>
                    <SelectTrigger data-testid={`dyn-filter-select-${f.key}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{f.label}</SelectItem>
                      {(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              );
            }
            if (f.type === 'text') {
              return (
                <div key={f.filter_id} data-testid={`dyn-filter-${f.key}`}>
                  {commonLabel}
                  <Input
                    value={dynamicValues[f.key] || ''}
                    onChange={(e) => setVal(f.key, e.target.value)}
                    placeholder={f.label}
                    className="border border-border text-sm"
                    data-testid={`dyn-filter-text-${f.key}`}
                  />
                </div>
              );
            }
            if (f.type === 'number') {
              return (
                <div key={f.filter_id} data-testid={`dyn-filter-${f.key}`}>
                  {commonLabel}
                  <NumberInput
                    value={dynamicValues[f.key] ?? ''}
                    onChange={(n) => setVal(f.key, n === '' ? '' : String(n))}
                    placeholder={f.label}
                    className="border border-border text-sm"
                    data-testid={`dyn-filter-number-${f.key}`}
                  />
                </div>
              );
            }
            if (f.type === 'range') {
              return (
                <div key={f.filter_id} data-testid={`dyn-filter-${f.key}`}>
                  {commonLabel}
                  <div className="flex gap-1">
                    <NumberInput
                      placeholder="Min"
                      value={dynamicValues[`min_${f.key}`] ?? ''}
                      onChange={(n) => setVal(`min_${f.key}`, n === '' ? '' : String(n))}
                      className="border border-border text-sm"
                      data-testid={`dyn-filter-min-${f.key}`}
                    />
                    <NumberInput
                      placeholder="Max"
                      value={dynamicValues[`max_${f.key}`] ?? ''}
                      onChange={(n) => setVal(`max_${f.key}`, n === '' ? '' : String(n))}
                      className="border border-border text-sm"
                      data-testid={`dyn-filter-max-${f.key}`}
                    />
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      </div>
    );
  };

  /* ─── Unified Filter Layout (Desktop & Mobile) ─── */
  const renderFilters = (isMobile = false) => (
    <div className="space-y-4" data-testid="filter-content">
      {/* ═══ ROW 1: Primary Search Controls ═══ */}
      <div data-testid="filter-row-1">
        {!isMobile && <p className="text-xs font-medium text-text_muted uppercase tracking-wider mb-2">{t('home.search')}</p>}
        <div className={isMobile ? 'space-y-3' : 'grid grid-cols-4 gap-3'}>
          <div className={isMobile ? '' : 'col-span-2'}>
            {isMobile && <Label className="text-xs text-text_muted mb-1 block">{t('home.search')}</Label>}
            <Input
              placeholder={t('home.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="border border-border focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid={isMobile ? 'search-input-mobile' : 'search-input'}
            />
          </div>
          <div>
            {isMobile && <Label className="text-xs text-text_muted mb-1 block">{t('listing.category')}</Label>}
            <Select value={category} onValueChange={handleCategoryChange}>
              <SelectTrigger data-testid={isMobile ? 'category-filter-mobile' : 'category-filter'}>
                <SelectValue placeholder={t('listing.category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listing.category')}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{getCategoryName(cat.value, t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            {isMobile && <Label className="text-xs text-text_muted mb-1 block">{t('listing.subcategory')}</Label>}
            <Select value={subcategory} onValueChange={setSubcategory} disabled={category === 'all'}>
              <SelectTrigger data-testid={isMobile ? 'subcategory-filter-mobile' : 'subcategory-filter'} className={category === 'all' ? 'opacity-50' : ''}>
                <SelectValue placeholder={t('listing.subcategory')}>
                  {subcategory !== 'all' ? getSubcategoryName(subcategory, t) : t('listing.subcategory')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listing.subcategory')}</SelectItem>
                {subcatOptions.map((sub) => (
                  <SelectItem key={sub.value} value={sub.value} data-testid={`subcategory-option-${sub.value}`}>
                    <span className="inline-flex items-center">
                      {getSubcategoryName(sub.value, t)}
                      <SubcategoryInfoIcon description={sub.description} />
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* ═══ ROW 2: Global Filters ═══ */}
      <div className="border-t border-border pt-4" data-testid="filter-row-2">
        {!isMobile && <p className="text-xs font-medium text-text_muted uppercase tracking-wider mb-2">{t('listing.filters')}</p>}
        <div className={isMobile ? 'space-y-3' : 'grid grid-cols-5 gap-3'}>
          <div>
            <Label className="text-xs text-text_muted mb-1 block">Min {t('listing.price')}</Label>
            <NumberInput placeholder="Min" value={minPrice} onChange={(n) => setMinPrice(n)} className="border border-border text-sm" data-testid="min-price-input" />
          </div>
          <div>
            <Label className="text-xs text-text_muted mb-1 block">Max {t('listing.price')}</Label>
            <NumberInput placeholder="Max" value={maxPrice} onChange={(n) => setMaxPrice(n)} className="border border-border text-sm" data-testid="max-price-input" />
          </div>
          <div>
            <Label className="text-xs text-text_muted mb-1 block">{t('realEstate.region')}</Label>
            {regions.length > 0 ? (
              <Select value={region} onValueChange={handleRegionChange}>
                <SelectTrigger data-testid="region-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('realEstate.region')}</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : <div className="h-10" />}
          </div>
          <div>
            <Label className="text-xs text-text_muted mb-1 block">{t('listing.city')}</Label>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid={isMobile ? 'city-filter-mobile' : 'city-filter'}>
                <SelectValue placeholder={t('listing.city')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listing.city')}</SelectItem>
                {cities.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-text_muted mb-1 block">{t('listing.sortBy')}</Label>
            <Select value={sortPrice} onValueChange={setSortPrice}>
              <SelectTrigger data-testid={isMobile ? 'sort-filter-mobile' : 'sort-filter'}>
                <SelectValue placeholder={t('listing.sortBy')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t('listing.sortBy')}</SelectItem>
                <SelectItem value="newest">{t('listing.sortNewest')}</SelectItem>
                <SelectItem value="oldest">{t('listing.sortOldest')}</SelectItem>
                <SelectItem value="price_asc">{t('listing.priceLowHigh')}</SelectItem>
                <SelectItem value="price_desc">{t('listing.priceHighLow')}</SelectItem>
                <SelectItem value="most_viewed">{t('listing.sortMostViewed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ═══ Pro sellers toggle (full-width row) ═══ */}
        <div className="pt-3 mt-3 border-t border-border flex items-center gap-3" data-testid="pro-only-row">
          <Switch
            id={isMobile ? 'pro-only-toggle-mobile' : 'pro-only-toggle'}
            checked={proOnly}
            onCheckedChange={setProOnly}
            data-testid="pro-only-toggle"
          />
          <Label
            htmlFor={isMobile ? 'pro-only-toggle-mobile' : 'pro-only-toggle'}
            className="flex items-center gap-1.5 text-sm cursor-pointer select-none"
          >
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-info-soft text-info"
              aria-hidden="true"
            >
              <Storefront size={12} weight="fill" />
              Pro
            </span>
            <span>{t('listing.proOnly')}</span>
          </Label>
        </div>
      </div>

      {/* ═══ Dynamic admin-defined filters (visible only after category/subcategory selection) ═══ */}
      {renderDynamicFilters(isMobile)}

      {/* ═══ ROW 3: Actions ═══ */}
      <div className="border-t border-border pt-4 flex gap-3" data-testid="filter-row-3">
        <Button
          onClick={handleSearch}
          className="bg-primary hover:bg-primary-hover text-white tracking-wide active:scale-95 px-6"
          data-testid={isMobile ? 'apply-filters-btn' : 'search-button'}
        >
          <MagnifyingGlass size={18} className="mr-2" weight="bold" />
          {t('common.search')}
        </Button>
        <Button variant="outline" onClick={clearFilters} className="border-border text-text_muted hover:text-text_main" data-testid="clear-filters-btn">
          <X size={16} className="mr-1.5" /> {t('listing.clearFilters') || 'Clear'}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-cream pb-20 md:pb-0">
      <Header />

      <div className="max-w-7xl mx-auto p-6">
        {user && (
          <Link to="/create">
            <div className="mb-6 bg-primary rounded-2xl p-4 md:p-5 flex items-center justify-between hover:bg-primary-hover transition-colors" data-testid="post-ad-cta">
              <div>
                <h3 className="text-white font-medium text-base">{t('home.startSelling')}</h3>
                <p className="text-white/80 text-sm">{t('home.freeListings')}</p>
              </div>
              <div className="bg-white/20 rounded-full p-3">
                <Plus size={24} className="text-white" weight="bold" />
              </div>
            </div>
          </Link>
        )}
        <h1 className="text-lg md:text-xl font-medium text-text_strong tracking-wide mb-6" data-testid="browse-title">
          {t('nav.browse')}
        </h1>

        {/* Desktop filters */}
        <div className="hidden md:block bg-white border border-border rounded-xl p-5 mb-6" data-testid="search-filters">
          {renderFilters(false)}
        </div>

        {/* Mobile: search + filter button */}
        <div className="md:hidden bg-white border border-border rounded-md p-3 mb-6" data-testid="search-filters-mobile">
          <div className="flex gap-2">
            <Input
              placeholder={t('home.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="border border-border flex-1"
              data-testid="search-input-mobile-bar"
            />
            <Button onClick={() => setShowMobileFilters(true)} variant="outline" className="border-border px-3" data-testid="mobile-filter-btn">
              <Faders size={20} />
            </Button>
            <Button onClick={handleSearch} className="bg-primary hover:bg-primary-hover text-white px-3" data-testid="search-button-mobile">
              <MagnifyingGlass size={20} weight="bold" />
            </Button>
          </div>
        </div>

        {/* Mobile Filter Panel */}
        {showMobileFilters && (
          <>
            <div className="fixed inset-0 z-[40] bg-black/40 md:hidden" onClick={() => setShowMobileFilters(false)} />
            <div className="fixed bottom-0 left-0 right-0 z-[45] bg-white rounded-t-2xl p-6 pb-24 max-h-[85vh] overflow-y-auto md:hidden" data-testid="mobile-filter-panel">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-medium text-base text-text_strong tracking-wide">{t('listing.filters') || 'Filters'}</h3>
                <button onClick={() => setShowMobileFilters(false)} className="text-text_muted hover:text-text_main" data-testid="close-mobile-filters">
                  <X size={24} />
                </button>
              </div>
              {renderFilters(true)}
            </div>
          </>
        )}

        {/* View mode toggle + Save Search action row.
            Toggle is always visible (it's a global UX preference);
            Save Search only shows for logged-in users with active filters. */}
        <div className="flex justify-end items-center gap-2 mb-3">
          <div
            className="inline-flex bg-surface border border-border rounded-full p-0.5"
            role="group"
            aria-label="View mode"
            data-testid="view-mode-toggle"
          >
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              aria-pressed={viewMode === 'grid'}
              title={t('common.gridView') || 'Grid view'}
              className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                viewMode === 'grid' ? 'bg-white text-primary shadow-sm' : 'text-text_muted hover:text-text_main'
              }`}
              data-testid="view-mode-grid"
            >
              <GridFour size={16} weight={viewMode === 'grid' ? 'fill' : 'regular'} />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              aria-pressed={viewMode === 'list'}
              title={t('common.listView') || 'List view'}
              className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                viewMode === 'list' ? 'bg-white text-primary shadow-sm' : 'text-text_muted hover:text-text_main'
              }`}
              data-testid="view-mode-list"
            >
              <ListIcon size={16} weight={viewMode === 'list' ? 'bold' : 'regular'} />
            </button>
          </div>
          {hasActiveFilters && user && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveSearch}
              className="text-xs gap-1.5"
              data-testid="save-search-btn"
            >
              <Star size={16} weight="bold" />
              {t('savedSearches.saveSearch')}
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12" data-testid="loading-state">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-text_muted">{t('common.loading')}</p>
          </div>
        ) : listings.length === 0 ? (
          <>
            <div className="text-center py-12" data-testid="no-results">
              <p className="text-text_muted text-lg">{t('common.noResults')}</p>
            </div>
            <RecentlyViewed />
          </>
        ) : (
          <>
          <div
            className={
              viewMode === 'list'
                ? 'flex flex-col gap-2'
                : 'grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5'
            }
            data-testid={viewMode === 'list' ? 'listings-list' : 'listings-grid'}
          >
            {listings.map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} variant={viewMode} />
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={goToPage} totalResults={totalResults} perPage={LISTINGS_PER_PAGE} />
          )}

          {/* Recently viewed — always at the bottom */}
          <div className="mt-10">
            <RecentlyViewed />
          </div>
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

/* ─── Pagination (numbered + prev/next, responsive) ─── */
const Pagination = ({ currentPage, totalPages, onPageChange, totalResults, perPage }) => {
  const windowSize = 5; // desktop page-number window
  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, currentPage - half);
  let end = Math.min(totalPages, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  const rangeStart = (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalResults);

  const base = "inline-flex items-center justify-center min-w-[36px] h-9 px-2 text-sm rounded-md border transition-colors";

  return (
    <nav className="mt-8 flex flex-col items-center gap-2" aria-label="Pagination" data-testid="pagination">
      {/* Desktop: numbered */}
      <div className="hidden md:flex items-center gap-1.5" data-testid="pagination-desktop">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`${base} border-border text-text_muted hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none`}
          data-testid="page-prev"
        >
          « Prev
        </button>
        {start > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className={`${base} border-border text-text_muted hover:border-primary`} data-testid="page-1">1</button>
            {start > 2 && <span className="text-text_muted px-1">…</span>}
          </>
        )}
        {pages.map(p => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            aria-current={p === currentPage ? 'page' : undefined}
            className={p === currentPage
              ? `${base} bg-primary text-white border-primary`
              : `${base} border-border text-text_muted hover:border-primary hover:text-primary`}
            data-testid={`page-${p}`}
          >
            {p}
          </button>
        ))}
        {end < totalPages && (
          <>
            {end < totalPages - 1 && <span className="text-text_muted px-1">…</span>}
            <button onClick={() => onPageChange(totalPages)} className={`${base} border-border text-text_muted hover:border-primary`} data-testid={`page-${totalPages}`}>
              {totalPages}
            </button>
          </>
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`${base} border-border text-text_muted hover:border-primary hover:text-primary disabled:opacity-40 disabled:pointer-events-none`}
          data-testid="page-next"
        >
          Next »
        </button>
      </div>

      {/* Mobile: compact */}
      <div className="flex md:hidden items-center gap-3" data-testid="pagination-mobile">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`${base} border-border text-text_muted disabled:opacity-40 disabled:pointer-events-none`}
          data-testid="page-prev-mobile"
        >
          « Prev
        </button>
        <span className="text-sm text-text_muted font-medium">
          Page <strong className="text-primary">{currentPage}</strong> of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`${base} border-border text-text_muted disabled:opacity-40 disabled:pointer-events-none`}
          data-testid="page-next-mobile"
        >
          Next »
        </button>
      </div>

      <p className="text-xs text-text_muted mt-1" data-testid="pagination-summary">
        {rangeStart}–{rangeEnd} of {totalResults}
      </p>
      <p className="hidden md:block text-[10px] text-text_muted/70 mt-0.5" data-testid="pagination-keyboard-hint">
        Tip: use <kbd className="px-1 py-0.5 bg-surface border border-border rounded text-[10px] font-mono">←</kbd> <kbd className="px-1 py-0.5 bg-surface border border-border rounded text-[10px] font-mono">→</kbd> to navigate pages
      </p>
    </nav>
  );
};
